import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import { useRef } from 'react';
import { useDrag } from 'react-dnd';
import { MdClose, MdDragIndicator } from 'react-icons/md';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MIcon,
  MText,
} from '../../../../../components/Monetize';
import {
  MANAGE_GROUPS_OFFERING_DRAG_TYPE,
  ManageGroupsOfferingDragItem,
} from './manageQuoteOfferingGroupsConstants';
import {
  ManageGroupsOfferingDetail,
  getQuoteOfferingDisplayName,
} from './manageQuoteOfferingGroupsUtils';

interface ManageQuoteOfferingGroupsChipProps {
  quoteOffering: IQuoteOfferingRespSchema;
  /** Term and amount — what tells two copies of the same offering apart */
  detail?: ManageGroupsOfferingDetail;
  /** Row the chip currently sits in; null for the Ungrouped strip */
  rowKey: string | null;
  /**
   * Whether this offering may change groups. False on a read-only quote, and for
   * offerings that carried over from the contract an amendment is amending.
   */
  isMovable?: boolean;
  /** Omitted for ungrouped chips — there is nowhere further to remove them to */
  onUngroup?: () => void;
}

/**
 * A group member, draggable onto another group row or the Ungrouped strip.
 *
 * Deliberately not an MTag: the same offering can appear on a quote more than
 * once with different terms, so the chip has to carry its dates and amount, and
 * a tag is a fixed single-line 20px.
 */
export const ManageQuoteOfferingGroupsChip = ({
  quoteOffering,
  detail,
  rowKey,
  isMovable = true,
  onUngroup,
}: ManageQuoteOfferingGroupsChipProps) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag<
    ManageGroupsOfferingDragItem,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: MANAGE_GROUPS_OFFERING_DRAG_TYPE,
      item: { quoteOfferingId: quoteOffering.id, fromRowKey: rowKey },
      canDrag: isMovable,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [quoteOffering.id, rowKey, isMovable],
  );
  drag(chipRef);

  const showRemove = isMovable && !!onUngroup;

  return (
    <MFlex
      ref={chipRef}
      align="center"
      gap={1}
      // Uniform full-width cards stacked vertically: a wrapping row of
      // content-width chips gave every offering a different size and put them in
      // ragged columns, which is the opposite of easy to scan.
      w="full"
      // Indigo-on-light-blue, the system's interactive pairing (same as the blue
      // Tag variant) — grey read as "disabled" when the whole point is that you
      // can pick this up and move it. An offering that genuinely can't move gets
      // exactly that grey, so the two states are told apart at a glance.
      bg={isMovable ? 'tBlue.hover' : 'bg.muted'}
      borderWidth="1px"
      borderColor={isMovable ? 'tBlue.lightShade' : 'border.subtle'}
      borderRadius="sm"
      pl={isMovable ? 1 : 2}
      pr={showRemove ? 0 : 2}
      py={1}
      cursor={isMovable ? 'grab' : 'default'}
      opacity={isDragging ? 0.4 : 1}
      data-testid={`manage-groups-chip-${quoteOffering.id}`}
    >
      {isMovable && <MIcon as={MdDragIndicator} color="tIndigo.base" />}
      {/* Takes the slack so the remove button sits at the card's right edge */}
      <MBox flex="1" minW={0}>
        <MText
          fontSize="xs"
          fontWeight={600}
          color={isMovable ? 'tIndigo.base' : 'fg.primary'}
          lineClamp={1}
        >
          {getQuoteOfferingDisplayName(quoteOffering)}
        </MText>
        {detail && (
          /*
           * Term and amount are separated by pushing them to opposite ends of
           * the card rather than by a separator character — the card is full
           * width, so the alignment does the work and the amounts of stacked
           * offerings line up in a column you can compare down.
           */
          <MFlex justify="space-between" gap={2} align="baseline">
            <MText fontSize="11px" color="fg.muted" lineClamp={1}>
              {detail.term}
            </MText>
            <MText
              fontSize="11px"
              color="fg.muted"
              fontWeight={600}
              whiteSpace="nowrap"
            >
              {detail.amount}
            </MText>
          </MFlex>
        )}
      </MBox>
      {showRemove && (
        <MCustomIconButton
          btnSize={3.5}
          containerSize={5}
          icon={MdClose}
          aria-label={`Remove ${getQuoteOfferingDisplayName(quoteOffering)} from this group`}
          onClick={onUngroup}
        />
      )}
    </MFlex>
  );
};
